import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { MedicalPaOrder } from "../../../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { CptCodeWithDetailsKeys } from "../../../../../enums/tableColumnKeys";
import {
  checkIfDateIsEarlierThanDateOfService,
  checkIfDateIsInCorrectFormat,
  checkIfDateIsLaterThanDateOfService,
  checkIfJCodeFieldHasError,
  checkIfNumVisitsMatchesRemainingAndCompleted,
  checkIfStringIsValidNumber,
} from "./computeErrorStates";
import { doModificationsRelatedToJCode } from "./doModificationsRelatedToJCode";

const checkIfEnteredValueIsGreaterThanZero = (value: string) => {
  return parseInt(value) > 0;
};

export const updateRowDataForDifferentKeys = (
  key: string,
  rowDataToUpdate: CptCodesTableRowModel,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  switch (key) {
    case CptCodeWithDetailsKeys.StartFrom:
      return updateRowDataForStartFrom(
        rowDataToUpdate,
        updatedData,
        medicalPaOrder,
      );

    case CptCodeWithDetailsKeys.EndAt:
      return updateRowDataForEndAt(
        rowDataToUpdate,
        updatedData,
        medicalPaOrder,
      );
    case CptCodeWithDetailsKeys.RemainingVisits:
      return updateRowDataForRemainingVisits(
        rowDataToUpdate,
        updatedData,
        medicalPaOrder,
      );
    case CptCodeWithDetailsKeys.VisitsAllowed:
      return updateRowDataForVisitsAllowed(
        rowDataToUpdate,
        updatedData,
        medicalPaOrder,
      );
    case CptCodeWithDetailsKeys.Visits:
      return updateRowDataForVisits(
        rowDataToUpdate,
        updatedData,
        medicalPaOrder,
      );
    case CptCodeWithDetailsKeys.PaRequired:
      return updateRowDataForPaRequired(
        rowDataToUpdate,
        updatedData,
        medicalPaOrder,
      );
    case CptCodeWithDetailsKeys.JCodes:
      return updateRowDataForJCodes(rowDataToUpdate, updatedData);
    default:
      return rowDataToUpdate;
  }
};

const updateRowDataForStartFrom = (
  rowDataToUpdate: any,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  const hasError =
    !checkIfDateIsInCorrectFormat(updatedData, "MM/DD/YYYY") ||
    !checkIfDateIsEarlierThanDateOfService(
      updatedData,
      medicalPaOrder?.payload?.date_of_service ?? "",
    );
  const isAuthOnFile =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthOnFile;
  const isAuthByRisa =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthByRISA;
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.StartFrom]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.StartFrom],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
  };
};

const updateRowDataForEndAt = (
  rowDataToUpdate: any,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  const hasError =
    !checkIfDateIsInCorrectFormat(updatedData, "MM/DD/YYYY") ||
    !checkIfDateIsLaterThanDateOfService(
      updatedData,
      medicalPaOrder?.payload?.date_of_service ?? "",
    );
  const isAuthOnFile =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthOnFile;
  const isAuthByRisa =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthByRISA;
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.EndAt]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.EndAt],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
  };
};

const updateRowDataForRemainingVisits = (
  rowDataToUpdate: any,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  const isAuthOnFile =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthOnFile;
  const isAuthByRisa =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthByRISA;
  const hasError =
    isAuthOnFile || isAuthByRisa
      ? !checkIfEnteredValueIsGreaterThanZero(updatedData) ||
        !checkIfStringIsValidNumber(updatedData)
      : false;
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.RemainingVisits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits],
      hasError: isAuthOnFile ? hasError : false,
      data: updatedData,
    },
    [CptCodeWithDetailsKeys.Visits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.Visits],
      hasError: isAuthOnFile
        ? !checkIfNumVisitsMatchesRemainingAndCompleted(
            parseInt(
              rowDataToUpdate[CptCodeWithDetailsKeys.Visits]?.data ?? "0",
            ),
            parseInt(updatedData),
            parseInt(
              rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed]?.data ??
                "0",
            ),
          )
        : false,
    },
  };
};

const updateRowDataForVisitsAllowed = (
  rowDataToUpdate: any,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  const hasError = !checkIfStringIsValidNumber(updatedData);
  const isAuthOnFile =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthOnFile;
  const isAuthByRisa =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthByRISA;
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.VisitsAllowed]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
    [CptCodeWithDetailsKeys.Visits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.Visits],
      hasError:
        isAuthOnFile || isAuthByRisa
          ? !checkIfNumVisitsMatchesRemainingAndCompleted(
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.Visits]?.data ?? "0",
              ),
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits]?.data ??
                  "0",
              ),
              parseInt(updatedData ?? "0"),
            )
          : false,
    },
  };
};

const updateRowDataForVisits = (
  rowDataToUpdate: any,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  const isAuthOnFile =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthOnFile;
  const isAuthByRisa =
    rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId ===
    MedicalPaOrdersAuthStatus.AuthByRISA;
  const hasError =
    !checkIfStringIsValidNumber(updatedData) ||
    !checkIfNumVisitsMatchesRemainingAndCompleted(
      parseInt(updatedData ?? "0"),
      parseInt(
        rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits]?.data ?? "0",
      ),
      parseInt(
        rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed]?.data ?? "0",
      ),
    );
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.Visits]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.Visits],
      hasError: isAuthOnFile || isAuthByRisa ? hasError : false,
      data: updatedData,
    },
  };
};

// Helper function to update field with error state
const updateFieldWithError = (
  rowData: any,
  fieldKey: string,
  hasError: boolean,
) => ({
  ...rowData[fieldKey],
  hasError,
});

const updateRowDataForPaRequired = (
  rowDataToUpdate: any,
  updatedData: any,
  medicalPaOrder: MedicalPaOrder,
) => {
  const isAuthOnFile =
    updatedData?.currentStatusId === MedicalPaOrdersAuthStatus.AuthOnFile;
  const isAuthByRisa =
    updatedData?.currentStatusId === MedicalPaOrdersAuthStatus.AuthByRISA;
  // Define the fields that need error validation
  const fieldsToUpdate = [
    CptCodeWithDetailsKeys.StartFrom,
    CptCodeWithDetailsKeys.EndAt,
    CptCodeWithDetailsKeys.RemainingVisits,
    CptCodeWithDetailsKeys.VisitsAllowed,
    CptCodeWithDetailsKeys.Visits,
  ];

  // Calculate error states only if AuthOnFile
  const errorStates =
    isAuthOnFile || isAuthByRisa
      ? {
          [CptCodeWithDetailsKeys.StartFrom]:
            !checkIfDateIsInCorrectFormat(
              rowDataToUpdate[CptCodeWithDetailsKeys.StartFrom]?.data ?? "",
              "MM/DD/YYYY",
            ) ||
            !checkIfDateIsEarlierThanDateOfService(
              rowDataToUpdate[CptCodeWithDetailsKeys.StartFrom]?.data ?? "",
              medicalPaOrder?.payload?.date_of_service ?? "",
            ),

          [CptCodeWithDetailsKeys.EndAt]:
            !checkIfDateIsInCorrectFormat(
              rowDataToUpdate[CptCodeWithDetailsKeys.EndAt]?.data ?? "",
              "MM/DD/YYYY",
            ) ||
            !checkIfDateIsLaterThanDateOfService(
              rowDataToUpdate[CptCodeWithDetailsKeys.EndAt]?.data ?? "",
              medicalPaOrder?.payload?.date_of_service ?? "",
            ),

          [CptCodeWithDetailsKeys.RemainingVisits]: !checkIfStringIsValidNumber(
            rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits]?.data ??
              "0",
          ),

          [CptCodeWithDetailsKeys.VisitsAllowed]: !checkIfStringIsValidNumber(
            rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed]?.data ?? "0",
          ),

          [CptCodeWithDetailsKeys.Visits]:
            !checkIfStringIsValidNumber(
              rowDataToUpdate[CptCodeWithDetailsKeys.Visits]?.data ?? "0",
            ) ||
            !checkIfNumVisitsMatchesRemainingAndCompleted(
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.Visits]?.data ?? "0",
              ),
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.RemainingVisits]?.data ??
                  "0",
              ),
              parseInt(
                rowDataToUpdate[CptCodeWithDetailsKeys.VisitsAllowed]?.data ??
                  "0",
              ),
            ),
        }
      : {};

  // Build the updated row data
  const updatedFields = fieldsToUpdate.reduce((acc, fieldKey) => {
    acc[fieldKey] = updateFieldWithError(
      rowDataToUpdate,
      fieldKey,
      isAuthOnFile || isAuthByRisa ? errorStates[fieldKey] : false,
    );
    return acc;
  }, {} as any);

  const updatedRowDataWithoutJCodeModifications = {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.PaRequired]: {
      ...updatedData,
    },
    ...updatedFields,
  };

  // Explictly calling the function to do modifications related to JCode
  const updatedRowDataWithJCodeModifications = doModificationsRelatedToJCode(
    updatedRowDataWithoutJCodeModifications,
  );
  return updatedRowDataWithJCodeModifications;
};

const updateRowDataForJCodes = (rowDataToUpdate: any, updatedData: any) => {
  return {
    ...rowDataToUpdate,
    [CptCodeWithDetailsKeys.JCodes]: {
      ...rowDataToUpdate[CptCodeWithDetailsKeys.JCodes],
      data: updatedData,
      hasError: checkIfJCodeFieldHasError(
        rowDataToUpdate[CptCodeWithDetailsKeys.PaRequired]?.currentStatusId,
        updatedData,
      ),
    },
  };
};
